import { useRef, useState } from "react";

import VariableList from "@/components/InputVariableConfigurator/ConfiguratorContent/VariableList";
import { Variable } from "@/components/InputVariableConfigurator/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ConversationSamplesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  samples: string[];
  onSamplesChange: (samples: string[]) => void;
  disabled?: boolean;
}

export const ConversationSamplesDialog = ({
  open,
  onOpenChange,
  samples,
  onSamplesChange,
  disabled = false,
}: ConversationSamplesDialogProps) => {
  const [localSamples, setLocalSamples] = useState<string[]>(samples);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleSave = () => {
    onSamplesChange(localSamples);
    onOpenChange(false);
  };

  const handleCancel = () => {
    setLocalSamples(samples); // Reset to original values
    onOpenChange(false);
  };

  // Update local samples when dialog opens with new samples
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setLocalSamples(samples);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[80vh] max-w-2xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Manage Conversation Samples</DialogTitle>
          <DialogDescription>
            Add conversation examples to help guide the conversation simulator. Each sample should
            represent a typical conversation flow.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4" ref={scrollContainerRef}>
          <VariableList
            showAddFileButton={false}
            scrollContainerRef={scrollContainerRef}
            lastItemRef={null}
            variables={localSamples.map((sample, index) => ({
              key: `Conversation Sample ${index + 1}`,
              value: sample,
              id: index.toString(),
            }))}
            placeholder="User: Hello!
Assistant: Hi there! How can I help you today?"
            tooltip="Conversation sample"
            hideKey
            updateInputVariable={(variable: Variable, index: number) => {
              const stringValue =
                typeof variable.value === "object"
                  ? JSON.stringify(variable.value)
                  : String(variable.value);

              setLocalSamples(currentSamples =>
                currentSamples.map((sample, i) => (i === index ? stringValue : sample)),
              );
            }}
            removeInputVariable={(index: number) => {
              setLocalSamples(currentSamples => currentSamples.filter((_, i) => i !== index));
            }}
            readOnly={disabled}
          />

          <div className="mt-4">
            <button
              type="button"
              onClick={() => setLocalSamples([...localSamples, ""])}
              disabled={disabled}
              className="w-full rounded-md border border-dashed border-gray-300 p-3 text-sm text-gray-500 hover:border-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              + Add Conversation Sample
            </button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
